import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import {
  CoreIconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { InfoBanner, InfoBannerSeverity } from "@b2bportal/core-ui";
import { Trans } from "@hopper-b2b/i18n";
import { FlightScheduleChangeModal } from "@tenant/flights";
import clsx from "clsx";

import { useScheduleChange } from "../../hooks";
import defaultStyles from "./FlightScheduleChangeBanner.module.scss";

export interface IFlightScheduleChangeBannerProps {
  itinerary: BookedFlightItineraryWithDepartureTime;
  hideActionButton?: boolean;
}

export const FlightScheduleChangeBanner = (
  props: IFlightScheduleChangeBannerProps
) => {
  const { itinerary, hideActionButton = false } = props;
  const COMPONENT_KEY = CoreFlightsComponent.FlightScheduleChangeBanner;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const scheduleChangeData = useScheduleChange(props.itinerary);

  if (scheduleChangeData == null) return null;

  const { context, copy } = scheduleChangeData;

  return (
    <div className={clsx(block)}>
      {context.hasMajorChange && context.diffModel != null && (
        <>
          <InfoBanner
            className={cn("major-change-banner")}
            icon={CoreIconNameEnum.warningCircle}
            severity={InfoBannerSeverity.ERROR}
          >
            <div className={cn("major-change-banner-content")}>
              <Trans
                components={[<b />]}
                i18nKey={copy.majorLabelKey}
                values={{ expDate: context.expDate }}
              />
            </div>
            {!hideActionButton && (
              <FlightScheduleChangeModal
                hoursToDeparture={props.itinerary.hoursToDeparture}
                itinerary={itinerary.bookedItinerary}
                scheduleChangeDiffs={context.diffModel}
                triggerCtaLabel={copy.scheduleChangeCtaLabel}
              />
            )}
          </InfoBanner>
          <InfoBanner
            className={cn("airline-warning-banner")}
            icon={CoreIconNameEnum.warningCircle}
            severity={InfoBannerSeverity.WARNING}
          >
            <span dangerouslySetInnerHTML={{ __html: copy.airlineWarning }} />
          </InfoBanner>
        </>
      )}
      {context.hasMinorChange && (
        <InfoBanner
          className={cn("minor-change-banner")}
          icon={CoreIconNameEnum.warningCircle}
          severity={InfoBannerSeverity.WARNING}
        >
          <Trans components={[<b />]} i18nKey={copy.minorLabelKey} />
        </InfoBanner>
      )}
    </div>
  );
};
